import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type {
  LabourCategory,
  Material,
  MaterialCategory,
  MaterialRequest,
  PageResponse,
  Project,
  ProjectCreateRequest,
  ProjectStatus,
  ProjectSummary,
  Site,
  SiteCreateRequest,
  SiteSummary,
  Uom,
  WorkPackage,
} from './types'

export const queryKeys = {
  projects: (params: unknown) => ['projects', params] as const,
  myProjects: ['projects', 'mine'] as const,
  project: (id: string) => ['projects', id] as const,
  sites: (params: unknown) => ['sites', params] as const,
  mySites: (projectId?: string) => ['sites', 'mine', projectId ?? null] as const,
  materials: (params: unknown) => ['materials', params] as const,
  uoms: ['masters', 'uoms'] as const,
  materialCategories: ['masters', 'material-categories'] as const,
  labourCategories: ['masters', 'labour-categories'] as const,
  workPackages: (projectId?: string) => ['masters', 'work-packages', projectId ?? null] as const,
}

// ------------------------------------------------------------------ projects

export interface ProjectListParams {
  q?: string
  status?: ProjectStatus | ''
  active?: boolean | ''
  page: number
  size: number
}

export function useProjects(params: ProjectListParams) {
  return useQuery({
    queryKey: queryKeys.projects(params),
    queryFn: async () => {
      const { data } = await api.get<PageResponse<ProjectSummary>>('/projects', {
        params: cleanParams(params),
      })
      return data
    },
  })
}

export function useMyProjects() {
  return useQuery({
    queryKey: queryKeys.myProjects,
    queryFn: async () => {
      const { data } = await api.get<ProjectSummary[]>('/projects/mine')
      return data
    },
  })
}

export function useCreateProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: ProjectCreateRequest) => {
      const { data } = await api.post<Project>('/projects', body)
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

export function useUpdateProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: ProjectCreateRequest }) => {
      const { data } = await api.put<Project>(`/projects/${id}`, body)
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

export function useDeleteProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/projects/${id}`)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

// --------------------------------------------------------------------- sites

export interface SiteListParams {
  projectId?: string
  q?: string
  status?: ProjectStatus | ''
  active?: boolean | ''
  page: number
  size: number
}

export function useSites(params: SiteListParams) {
  return useQuery({
    queryKey: queryKeys.sites(params),
    queryFn: async () => {
      const { data } = await api.get<PageResponse<SiteSummary>>('/sites', {
        params: cleanParams(params),
      })
      return data
    },
  })
}

export function useMySites(projectId?: string) {
  return useQuery({
    queryKey: queryKeys.mySites(projectId),
    queryFn: async () => {
      const { data } = await api.get<SiteSummary[]>('/sites/mine', {
        params: projectId ? { projectId } : undefined,
      })
      return data
    },
  })
}

export function useCreateSite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: SiteCreateRequest) => {
      const { data } = await api.post<Site>('/sites', body)
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sites'] })
    },
  })
}

export function useUpdateSite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: SiteCreateRequest }) => {
      const { data } = await api.put<Site>(`/sites/${id}`, body)
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sites'] })
    },
  })
}

export function useDeleteSite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/sites/${id}`)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sites'] })
    },
  })
}

// --------------------------------------------------------------- master data

export interface MaterialListParams {
  q?: string
  categoryId?: string
  page: number
  size: number
}

export function useMaterials(params: MaterialListParams) {
  return useQuery({
    queryKey: queryKeys.materials(params),
    queryFn: async () => {
      const { data } = await api.get<PageResponse<Material>>('/masters/materials', {
        params: cleanParams(params),
      })
      return data
    },
  })
}

export function useCreateMaterial() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: MaterialRequest) => {
      const { data } = await api.post<Material>('/masters/materials', body)
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['materials'] })
    },
  })
}

export function useUpdateMaterial() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: MaterialRequest }) => {
      const { data } = await api.put<Material>(`/masters/materials/${id}`, body)
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['materials'] })
    },
  })
}

export function useDeleteMaterial() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/masters/materials/${id}`)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['materials'] })
    },
  })
}

export function useUoms() {
  return useQuery({
    queryKey: queryKeys.uoms,
    queryFn: async () => {
      const { data } = await api.get<Uom[]>('/masters/uoms')
      return data
    },
    staleTime: 10 * 60 * 1000,
  })
}

export function useMaterialCategories() {
  return useQuery({
    queryKey: queryKeys.materialCategories,
    queryFn: async () => {
      const { data } = await api.get<MaterialCategory[]>('/masters/material-categories')
      return data
    },
    staleTime: 10 * 60 * 1000,
  })
}

export function useLabourCategories() {
  return useQuery({
    queryKey: queryKeys.labourCategories,
    queryFn: async () => {
      const { data } = await api.get<LabourCategory[]>('/masters/labour-categories')
      return data
    },
    staleTime: 10 * 60 * 1000,
  })
}

export function useWorkPackages(projectId?: string) {
  return useQuery({
    queryKey: queryKeys.workPackages(projectId),
    queryFn: async () => {
      const { data } = await api.get<WorkPackage[]>('/masters/work-packages', {
        params: projectId ? { projectId } : undefined,
      })
      return data
    },
    staleTime: 10 * 60 * 1000,
  })
}

/** Drops empty strings and undefined so they never reach the API as literal "" filters. */
function cleanParams(params: object): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== '' && value !== undefined && value !== null),
  )
}
